import { useCallback, useContext, useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Address, erc20Abi, erc721Abi } from "viem"
import { useWalletClient } from "wagmi"

import { MEZO_CHAIN } from "#/shared/constants"
import { USDT } from "#/shared/constants/artifacts"
import { FlowContext } from "#/shared/context"

import { useWriteContractAndWaitForTransaction } from "../contract"

import useAllowanceERC20Token from "./useAllowanceERC20Token"

export default function useApproveERC20Token(
  tokenAddress: Address,
  spender: Address,
  chainId: number = MEZO_CHAIN.id,
) {
  const queryClient = useQueryClient()

  const {
    isWaitingForTxReceipt,
    isTxConfirmed,
    writeContractAsync,
    txHash: approveTxHash,
    ...rest
  } = useWriteContractAndWaitForTransaction(chainId)

  const {
    data: approvedAmount,
    queryKey,
    isFetching: isFetchingApprovedAmount,
    refetch: refetchApprovedAmount,
  } = useAllowanceERC20Token(tokenAddress, spender, chainId)

  useEffect(() => {
    if (isTxConfirmed) {
      refetchApprovedAmount()
    }
  }, [isTxConfirmed, refetchApprovedAmount])

  const { data: walletClient } = useWalletClient()

  const { isBitcoinFlow } = useContext(FlowContext)

  const approve = useCallback(
    async (amount: bigint) => {
      const allowance = await queryClient.fetchQuery<bigint>({ queryKey })
      if (allowance >= amount) return

      await writeContractAsync({
        abi: tokenAddress === USDT.address ? erc721Abi : erc20Abi,
        address: tokenAddress,
        functionName: "approve",
        args: [spender, amount],
        chainId,
        // TODO: try to extract it to `useWriteContractAndWaitForTransaction`
        // to not repeat this in every custom hook.
        // If this is a Bitcoin flow, we do not want to simulate the contract
        // as this will most likely fail since the associated EVM address has
        // zero balance on a given chain.
        __mode: isBitcoinFlow ? "prepared" : undefined,
        // We should use the wallet client object because it has
        // `sendTransaction` function, which will be fired. The
        // orangekit/passport lib creates a custom wallet client with an
        // overridden `sendTransaction` function. It sends the transaction via
        // Orangekit smart contracts.
        account: isBitcoinFlow ? walletClient?.account : undefined,
      })
    },
    [
      writeContractAsync,
      tokenAddress,
      spender,
      queryKey,
      queryClient,
      chainId,
      isBitcoinFlow,
      walletClient?.account,
    ],
  )

  return {
    approve,
    approvedAmount,
    isFetchingApprovedAmount,
    isWaitingForTxReceipt,
    isTxConfirmed,
    approveTxHash,
    ...rest,
  }
}
